'use client';

import React, { type HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import { useTranslation } from '@/app/components/translations/Translation';
import { Icon } from '@/app/common/icons/Icon';
import type { EvoFacilityCategory } from './buildFacilityCategories';
import { type EvoFacilityChip, useEvoFacilities } from './useEvoFacilities';

type EvoHotelFacilitiesProps = HTMLAttributes<HTMLElement> & {
  // Force a single column at every width. Defaults to false, i.e. two columns from `sm` up.
  singleColumn?: boolean;
};

const MostPopularTitle = styled(Text).attrs({ variant: 'Title5', forceTag: 'h3' })<{ $singleColumn?: boolean }>`
  /* space150 (12px) below the heading on mobile/single-column, space100 (8px) otherwise */
  margin-bottom: ${({ theme, $singleColumn }) =>
    $singleColumn ? theme.kallisto.space.space150 : theme.kallisto.space.space100};
`;

const Separator = styled.hr`
  margin: 24px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
`;

// Full list in two columns from `sm` up (single column below, or always when singleColumn is set).
const CategoriesGrid = styled.div<{ $singleColumn?: boolean }>`
  /* space500 between the two columns */
  column-gap: ${({ theme }) => theme.kallisto.space.space500};

  ${({ $singleColumn }) =>
    !$singleColumn &&
    css`
      @media ${customCss.breakpoints.sm} {
        column-count: 2;
      }
    `}
`;

// GENERAL is the long catch-all category, so let it flow across columns; every other
// category stays intact within a single column.
const CategoryContainer = styled.div<{ $breakColumn: boolean; $singleColumn?: boolean }>`
  display: flex;
  flex-direction: column;
  /* space150 (12px) on mobile/single-column, space200 (16px) otherwise, between the title and its list */
  gap: ${({ theme, $singleColumn }) => ($singleColumn ? theme.kallisto.space.space150 : theme.kallisto.space.space200)};
  /* space300 between stacked categories within a column */
  padding-bottom: ${({ theme }) => theme.kallisto.space.space300};

  ${({ $breakColumn }) => ($breakColumn ? 'break-inside: auto' : 'break-inside: avoid')};
`;

// h3: one level under the section's h2 title. Title5 only sets the type size.
const CategoryTitle = styled(Text).attrs({ variant: 'Title5', forceTag: 'h3' })`
  display: flex;
  align-items: center;
  /* space100 between the icon and the label */
  gap: ${({ theme }) => theme.kallisto.space.space100};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ServiceList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  /* space50 between services */
  li:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.kallisto.space.space50};
  }
`;

// The compact preview row, each entry with its category icon (see useEvoFacilities for what goes in it).
export function MostPopularRow({ chips }: { chips: EvoFacilityChip[] }) {
  return (
    <ul
      css={css`
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        /* space100 (8px) row gap, space250 (20px) column gap */
        gap: 8px 20px;
        margin: 0;
        padding: 0;
        list-style: none;
      `}
    >
      {chips.map((chip) => (
        <li
          key={chip.key}
          css={`
            display: flex;
            gap: 8px;
            align-items: center;
          `}
        >
          <Icon {...chip.icon} size={20} />
          <Text variant="Body">{chip.label}</Text>
        </li>
      ))}
    </ul>
  );
}

// A single category: its heading (icon + label) and its list of services in Body-regular text,
// with no per-service check icon.
function FacilitiesCategory({ category, singleColumn }: { category: EvoFacilityCategory; singleColumn?: boolean }) {
  return (
    <CategoryContainer $breakColumn={category.breakColumn} $singleColumn={singleColumn}>
      <CategoryTitle>
        <Icon {...category.icon} size={20} />
        {category.title}
      </CategoryTitle>
      <ServiceList>
        {category.items.map((item) => (
          <li key={item}>
            <Text variant="Body">{item}</Text>
          </li>
        ))}
      </ServiceList>
    </CategoryContainer>
  );
}

/**
 * A simplified facilities section: the "Most popular" preview row plus the full list of
 * facilities grouped by category, shown inline. Compared with {@link HotelPageFacilitiesSection}
 * it drops the "see more" modal / show-more button, removes the per-service check icon, and
 * renders services in Body-regular text.
 */
export default function EvoHotelFacilities({ singleColumn, ...props }: EvoHotelFacilitiesProps) {
  const { t } = useTranslation();
  const { mostPopular, categories } = useEvoFacilities();

  if (categories.length === 0) return null;

  return (
    <HotelPageContentSection {...props} title={t('vacation.select.facilities.title')}>
      {mostPopular.length > 0 && (
        <>
          <MostPopularTitle $singleColumn={singleColumn}>{t('vacation.select.facilities.popular')}</MostPopularTitle>
          <MostPopularRow chips={mostPopular} />
          <Separator />
        </>
      )}
      <CategoriesGrid $singleColumn={singleColumn}>
        {categories.map((category) => (
          <FacilitiesCategory key={category.key} category={category} singleColumn={singleColumn} />
        ))}
      </CategoriesGrid>
    </HotelPageContentSection>
  );
}
